import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import DrawerSide from '../stories/layout-components/DrawerSide';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import moment from 'moment';
import * as drawerActions from '../redux/actions-v2/drawer-actions';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { setGeneralStatus } from '../redux/actions-v2/coreAction';
import { IconButton, Box } from '@mui/material';
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AddAppointment from './AddAppointment';

const AgendaCalendarSidebar = ({ closeSidebar, locationId, isDrawerOpen, handleDrawerClose }) => {
  const { t, i18n } = useTranslation();
  const currentLangCode = i18n.language;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPlatform = useSelector(state => state.core.currentPlatform);
  const [agendaItems, setAgendaItems] = useState([]);
  const calendarRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [createAppointment, setCreateAppointment] = useState(false);
  const timeoutRef = useRef(null);

  const businessPreference = useSelector((state) => state.core.businessData);
  const businessStructure = useSelector(
    (state) => state.core.businessStructure
  );
  const currentUser = useSelector((state) => state.core.user);

  const passeStructure = businessStructure?.structures?.find(
    (structure) => structure.collectionField === 'passes'
  );

  const appointmentModule = businessStructure?.modules?.find(
    (module) => module?.list?.tabs?.[0]?.listType === 'calendar'
  );

  const getLists = useCallback(async (date) => {
    try {
      const lists = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `coreMultiV2/list`,
        body: {
          activeTab: 0,
          lang: currentLangCode?.slice(0, 2) || 'fr',
          moduleId: appointmentModule?.id,
          numericFilters: [
            "startDate:" + moment(date).startOf('day').unix() + " TO " + moment(date).endOf('day').unix()
          ],
          order: 'desc',
          orderBy: 'startDate',
          structureId: passeStructure?.id
        },
      });

      const formattedEvents = lists?.hits?.map(item => {
        const fieldsStructure = businessStructure?.structures?.find(
          (s) => s.id === item?.structureId
        )?.fields;

        const statusField = fieldsStructure?.find(
          (field) => field?.typeData === 'status'
        );

        const color = statusField?.selections?.find(
          (selection) => selection?.value === item?.status
        )?.color;

        return {
          id: item.id,
          title: item.name || '',
          start: new Date(item.startDate._seconds * 1000),
          end: new Date(item.endDate._seconds * 1000),
          color: color || '#696969',
          documentPath: item.documentPath,
          structureId: item.structureId,
          card: item.dependencyId || item.dependencyDetails?.id || {},
          depStructureId: item.dependencyDetails?.structureIdentifiant,
          targetProfileName: item.targetProfileName || '',
          locationName: item.locationDetails?.name || '',
          subTitle: item.targetName || '',
          status: item.status || 0,
          extendedProps: {
            targetProfileName: item.targetProfileName,
            targetName: item.targetName,
            locationName: item.locationDetails?.name,
            duration: item.duration,
            targetDetails: item.targetDetails,
            targetProfileDetails: item.targetProfileDetails,
            dependencyDetails: item.dependencyDetails,
            id: item.id,
            card: item.dependencyId || item.dependencyDetails?.id || {},
            depStructureId: item.dependencyDetails?.structureIdentifiant
          }
        };
      });

      setAgendaItems(formattedEvents);
    } catch (error) {
      console.error('Error fetching data: ', error);
      dispatch(setGeneralStatus({ status: 'error', error: error }));
    }
  }, [t, currentLangCode, appointmentModule, passeStructure, businessStructure, dispatch]);

  useEffect(() => {
    if (passeStructure && appointmentModule) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        getLists(currentDate);
      }, 100);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentDate, passeStructure, appointmentModule, getLists]);



  const handleEventClick = (info) => {
    const extendedValues = info?.event?._def?.extendedProps;
    const collectionMatch = businessStructure?.structures?.find(
      (s) => s.id === extendedValues?.depStructureId
    );
    const collectionField = collectionMatch?.collectionField || 'cardsuninvoiced';

    if (
      collectionField &&
      extendedValues?.depStructureId &&
      extendedValues?.card
    ) {
      dispatch(drawerActions.sidebarAgenda({ isDrawerOpen: false, show: false }));
      navigate(
        `/bos/element/${collectionField}/${extendedValues?.depStructureId}/${extendedValues?.card}`
      );
    }
  };

  const handleDateChange = (date) => {
    setCurrentDate(date);
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(date);
    }
  };

  const handleRefresh = () => {
    setCreateAppointment(false);
    getLists(currentDate);
  };

  const handleSlotClick = (info) => {

    setSelectedDate({
      startTime: moment(info.start).toDate(),
      endTime: moment(info.end).toDate()
    });
    setCreateAppointment(true);
  };

  const handlePrevDay = () => {
    const newDate = moment(currentDate).subtract(1, 'day').toDate();
    handleDateChange(newDate);
  };

  const handleNextDay = () => {
    const newDate = moment(currentDate).add(1, 'day').toDate();
    handleDateChange(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    handleDateChange(today);
  };

  const renderEventContent = (eventInfo) => {
    return (
      <div style={{ position: 'relative' }}>
        <div>
          {moment(eventInfo.event?.start).format('HH:mm')} -
          {moment(eventInfo.event?.end).format('HH:mm')}
        </div>
        <div style={{ fontWeight: 500, fontSize: '0.8em' }}>
          {eventInfo.event.title +
            ' - ' +
            eventInfo.event.extendedProps?.targetProfileName || ''}
        </div>
        {eventInfo.event.extendedProps?.targetName && (
          <div style={{ fontSize: '0.7em' }}>
            {eventInfo.event.extendedProps.targetName}
          </div>
        )}
        {eventInfo.event.extendedProps?.locationName && (
          <div style={{ fontSize: '0.7em', color: '#666' }}>
            {eventInfo.event.extendedProps.locationName}
          </div>
        )}
      </div>
    );
  };

  const renderHeaderToolbar = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevDay} size="small">
            <ChevronLeft />
          </IconButton>
          <IconButton onClick={handleToday} size="small">
            <Today />
          </IconButton>
          <IconButton onClick={handleNextDay} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          {moment(currentDate).format('dddd D MMMM YYYY')}
        </Box>
        <Box sx={{ width: 120 }} /> {/* Spacer for balance */}
      </Box>
    );
  };


  return (
    <DrawerSide
      title={t('todayAgenda')}
      handleDrawerClose={closeSidebar}
      isDrawerOpen={isDrawerOpen}
      position='right'
      isCreation={true}
      width={createAppointment ? '1200px' : '650px'}
    >
      <div className='row' style={{ height: '75vh', maxHeight: '75vh' }}>
        {createAppointment && (
          <div className='col-6' >
            <AddAppointment
              startDate={moment(selectedDate?.startTime)}
              HandleCloseSidebar={handleDrawerClose}
              defaultLocationId={null}
              toSelect={true}
              handleRefresh={handleRefresh}
            />
          </div>
        )}
        <div className={createAppointment ? 'col-6' : 'col-12'} >
          {renderHeaderToolbar()}
          <FullCalendar
            ref={calendarRef}
            locale={frLocale}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridDay"
            headerToolbar={false}
            customButtons={{
              prev: {
                text: 'Previous',
                click: handlePrevDay
              },
              next: {
                text: 'Next',
                click: handleNextDay
              },
              today: {
                text: 'Today',
                click: handleToday
              }
            }}
            height="70vh"
            events={agendaItems}
            eventClick={handleEventClick}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false,
              hour12: false
            }}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            allDaySlot={false}
            nowIndicator={true}
            eventDisplay="block"
            initialDate={currentDate}
            date={currentDate}
            startTime={moment().format('HH:mm:ss')}
            endTime="23:00:00"
            slotDuration="00:15:00"
            eventContent={renderEventContent}
            datesSet={(dateInfo) => {
              setCurrentDate(dateInfo.start);
            }}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            select={handleSlotClick}
          />
        </div>
      </div>
    </DrawerSide>
  );
};

export default AgendaCalendarSidebar;
